/**
 * Le cycle de vie EFFECTIF — déclaré par le template, sinon le cycle intégré.
 *
 * Même contrat que `workflowFromTemplate` dans `useEffectiveTemplate` : le retour est TOUJOURS
 * défini et jamais vide. Un template sans `lifecycle`, ou dont le `lifecycle` est vide, retombe
 * sur `LIFECYCLE` (lib/lifecycle.ts) — c'est-à-dire le comportement d'avant, à l'identique.
 *
 * L'HÉRITAGE PAR CLÉ ÉVITE DE TOUT REDÉCLARER
 * -------------------------------------------
 * Une étape qui reprend une clé connue (`idee`, `spec`, `backlog`…) et ne précise ni route ni
 * icône hérite de celles de l'étape intégrée. Renommer « Spécification » en « Dossier de
 * conception » ne doit pas coûter la ré-saisie des sept routes — et une route oubliée
 * produirait un rail dont les étapes ne mènent nulle part.
 */
import type { LucideIcon } from 'lucide-react'

import type { TemplateLifecycleStage } from '@/api/client'
import { LIFECYCLE } from '@/lib/lifecycle'
import { resoudreIcone, ICONE_REPLI } from '@/lib/kindIcons'

export interface EtapeCycleDeVie {
  key: string
  label: string
  /** Route où l'on agit sur cette étape ; absente → l'étape n'est pas un lien. */
  to?: string
  /** Nom d'icône déclaré ; `iconeDEtape` le résout, avec repli sur l'étape connue. */
  icon?: string
  /**
   * `true` UNIQUEMENT quand l'étape vient du template chargé, et non du repli intégré.
   *
   * Même rôle que `workflowFromTemplate` : il dit au rail si le LIBELLÉ fait autorité. Un
   * libellé déclaré par l'organisation ne doit PAS repasser par `t('lifecycleRail.<clé>')` —
   * cela réafficherait « Spécification » là où elle a écrit « Dossier de conception ». À
   * l'inverse, le repli DOIT être traduit, sans quoi le rail resterait en français.
   */
  duTemplate: boolean
}

const PAR_CLE = new Map(LIFECYCLE.map((s) => [s.key as string, s]))

/** Le cycle intégré, projeté dans la forme des étapes de template. Calculé une seule fois. */
const REPLI: EtapeCycleDeVie[] = LIFECYCLE.map((s) => ({
  key: s.key, label: s.label, to: s.to, duTemplate: false,
}))

/**
 * Les étapes à afficher. `declarees` vide ou absent → le cycle intégré, jamais du vide.
 * Une étape déclarée sans libellé retombe sur celui de l'étape connue, puis sur sa clé : un
 * rail dont une pastille serait muette ne dirait plus où l'on est.
 */
export function etapesEffectives(
  declarees: readonly TemplateLifecycleStage[] | undefined,
): EtapeCycleDeVie[] {
  if (!declarees || declarees.length === 0) return REPLI
  return declarees.map((e): EtapeCycleDeVie => {
    const connue = PAR_CLE.get(e.key)
    const to = e.to ?? connue?.to
    return {
      key: e.key,
      label: e.label || connue?.label || e.key,
      duTemplate: true,
      // `exactOptionalPropertyTypes` : on ne pose la clé que si elle a une valeur.
      ...(to ? { to } : {}),
      ...(e.icon ? { icon: e.icon } : {}),
    }
  })
}

/** L'icône d'une étape : celle déclarée, sinon celle de l'étape connue, sinon le repli. */
export function iconeDEtape(etape: EtapeCycleDeVie): LucideIcon {
  return resoudreIcone(etape.icon ?? '') ?? PAR_CLE.get(etape.key)?.icon ?? ICONE_REPLI
}
